import { Lock, LockOpen } from 'lucide-react';
import { cn } from '@/lib/utils';
import { deriveLockState } from '../../activeLoanModel';
import type { ProspectLoan } from '../../types';

/**
 * Compact lock-status pill with urgency coloring.
 *   unlocked → subtle, no background
 *   locked (>5d out) → success tint
 *   locked + within 5d → destructive tint (lock_expiring)
 *   expired/busted → destructive tint with "Expired · Nd" label
 *   lock met (delivered on time) → success tint with "Met" label
 */
interface LockStatusPillProps {
  loan: Pick<ProspectLoan,
    | 'rate_lock_status'
    | 'rate_lock_buy_side_lock_expires'
    | 'shipping_investor_delivery_date'
  >;
  className?: string;
}

export function LockStatusPill({ loan, className }: LockStatusPillProps) {
  const { isCurrentlyLocked, isLockMet, isLockExpired, daysUntilExpiration } = deriveLockState(loan);

  if (!isCurrentlyLocked && !isLockMet && !isLockExpired && daysUntilExpiration === null) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 text-[11px] text-muted-foreground',
          className
        )}
      >
        <LockOpen className="h-3 w-3" aria-hidden />
        Unlocked
      </span>
    );
  }

  if (isLockExpired) {
    const daysExpired = daysUntilExpiration === null ? null : Math.max(0, -daysUntilExpiration);
    const label = daysExpired === null
      ? 'Expired'
      : daysExpired === 0
        ? 'Expired today'
        : `Expired · ${daysExpired}d`;
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 whitespace-nowrap rounded-sm bg-destructive/10 px-1.5 py-0.5 text-[11px] font-medium text-destructive',
          className
        )}
      >
        <Lock className="h-3 w-3" aria-hidden />
        {label}
      </span>
    );
  }

  if (isLockMet && !isCurrentlyLocked) {
    return (
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-sm bg-[hsl(142_71%_45%/0.12)] px-1.5 py-0.5 text-[11px] font-medium text-[hsl(142_71%_25%)]',
          className
        )}
      >
        <Lock className="h-3 w-3" aria-hidden />
        Met
      </span>
    );
  }

  const urgent = isCurrentlyLocked && daysUntilExpiration !== null && daysUntilExpiration <= 5;
  const label = daysUntilExpiration === null
    ? 'Locked'
    : daysUntilExpiration === 0
      ? 'Today'
      : `${daysUntilExpiration}d`;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-[11px] font-medium',
        urgent
          ? 'bg-destructive/10 text-destructive'
          : 'bg-[hsl(142_71%_45%/0.12)] text-[hsl(142_71%_25%)]',
        className
      )}
    >
      <Lock className="h-3 w-3" aria-hidden />
      <span className="font-mono">{label}</span>
    </span>
  );
}
